import {gql, QueryOptions} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as navigation from 'Navigation';
import {runURL} from 'results/assays/AssayRecordDAO';

import {RunQuery, RunQueryVariables} from './__generated/RunQuery';
import RunDetailsTabState from './RunDetailsTabState';
import RunResultsTabState from './RunResultsTabState';

export const RUN_QUERY: DocumentNode = gql`
  query RunQuery($runId: StrictString!) @skipAudit @engComponent(name: LAB_AUTOMATION) {
    run(id: $runId) {
      ... on RunNode {
        id
        apiId
        origin {
          id
          elnOrigin {
            entry {
              id
            }
            tableName
          }
          type
        }
        permissions {
          ...ListingPermissionsGraphQL
        }
        schema {
          id
          name
          automationInputFileConfigsJson
          automationOutputFileConfigsJson
          connectionSchema {
            id
            name
          }
        }
        connection {
          ...ConnectionListingGraphQL
        }
        equipment {
          ...BioentitySummaryGraphQL
        }
        studyIds
        analysisIds
        createdAt
        tagsJson
        shouldRecordEquipment
        automationInputFiles {
          ...AutomationInputFileGraphQL
        }
        automationOutputFiles {
          ...AutomationOutputFileGraphQL
        }
        automationErrorFiles {
          ...AutomationErrorFileGraphQL
        }
        isInEntry
      }
      ...HasArchiveRecordGraphQL
      ...HasCreatorGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ListingPermissions').fragment}
  ${getFragmentWrapper('HasCreator').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('ConnectionListing').fragment}
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('AutomationInputFile').fragment}
  ${getFragmentWrapper('AutomationOutputFile').fragment}
  ${getFragmentWrapper('AutomationErrorFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type Run = ExtractByTypename<RunQuery['run'], 'RunNode'>;

interface RunFileStateAttributes {
  run?: Run;
  name?: string;
  isLegacyRun?: boolean;
}

class RunFileState extends ApolloFileState<RunQuery, RunQueryVariables, RunFileStateAttributes> {
  static fileType = ModelTypeConstants.ASSAY_RUN;
  static override openFile(fileId: string): JQueryPromise<RunFileState> {
    return super.openFile(fileId) as JQueryPromise<RunFileState>;
  }

  static override getQueryOptions(fileId: string): QueryOptions<RunQueryVariables> {
    return {
      query: RUN_QUERY,
      variables: {
        runId: fileId,
      },
    };
  }

  static override dataToAttributes(data: RunQuery): RunFileStateAttributes {
    const {run} = data;
    return isInaccessible(run)
      ? {}
      : {
          run,
          name: run.schema.name,
          isLegacyRun:
            !run.schema.automationInputFileConfigsJson.length &&
            !run.schema.automationOutputFileConfigsJson.length,
        };
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
    };
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override initialize(...args: any): void {
    super.initialize(...args);
    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(new RunDetailsTabState({runId: this.id}));
    buffer.tabs().at(0).set({active: true});
    if (this.get('isLegacyRun')) {
      buffer.tabs().add(new RunResultsTabState({runId: this.id}));
    }
  }

  override editURL(): string {
    return runURL(this.id);
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }
}

export default RunFileState;
